import { ethers } from 'ethers';
import { 
  SpecializedCapsule,
  CreateFilmCapsuleInput,
  CreateCreatorMessageInput,
  CreateMysteryTraitInput,
  CreateVIPAccessInput,
  CreateArtProvenanceInput,
  CreateVotingCapsuleInput,
  CreateMusicCapsuleInput,
  CreateAchievementCapsuleInput,
  CreateEducationalCapsuleInput,
  CreateMemoryCapsuleInput,
  PaginationParams,
  PaginatedResponse,
  SpecializedCapsuleType
} from '../types';
import { LegacyChainClient } from '../core/LegacyChainClient';

const LEGACY_SPECIALIZED_CAPSULES_ABI = [
  'function createFilmCapsule(address recipient, string memory title, string memory director, uint256 premiereDate, string memory trailerURI, string memory encryptedFilmURI, string memory tokenURI) public returns (uint256)',
  'function createCreatorMessageCapsule(address recipient, string memory creator, string memory encryptedMessage, string memory publicTeaser, uint256 revealTime, string memory tokenURI) public returns (uint256)',
  'function createMysteryTraitCapsule(address recipient, string memory name, string[] memory hiddenTraits, uint256[] memory unlockTimes, string memory tokenURI) public returns (uint256)',
  'function createVIPAccessCapsule(address recipient, string memory eventId, string memory description, string memory encryptedAccessData, uint256 expiryTime, uint256 usageLimit, string memory tokenURI) public returns (uint256)',
  'function createArtProvenanceCapsule(address recipient, string memory artist, string memory baseArtworkURI, string[] memory layerURIs, string[] memory layerDescriptions, uint256[] memory revealTimes, string memory tokenURI) public returns (uint256)',
  'function createVotingCapsule(address voter, string memory proposalTitle, string[] memory options, uint256 endTime, uint256 voteWeight, string memory tokenURI) public returns (uint256)',
  'function createMusicCapsule(address recipient, string memory trackTitle, string memory artist, string memory encryptedTrackURI, string memory previewURI, uint256 releaseDate, string memory tokenURI) public returns (uint256)',
  'function createAchievementCapsule(address player, string memory achievementName, string[] memory tiers, uint256[] memory requirements, string[] memory rewards, string memory tokenURI) public returns (uint256)',
  'function createEducationalCapsule(address student, string memory courseName, string[] memory modules, uint256[] memory unlockDays, string[] memory contentURIs, string memory tokenURI) public returns (uint256)',
  'function createMemoryCapsule(address recipient, string memory title, string memory encryptedMemoryURI, uint256 unlockTime, address[] memory inheritors, string memory tokenURI) public returns (uint256)',
  'function getCapsuleType(uint256 tokenId) public view returns (uint8)',
  'function getFilmCapsule(uint256 tokenId) public view returns (string memory title, string memory director, uint256 premiereDate, string memory trailerURI, string memory encryptedFilmURI, uint256 viewCount, bool isReleased)',
  'function unlockTrait(uint256 tokenId, uint256 traitIndex) public',
  'function useVIPAccess(uint256 tokenId) public returns (string memory)',
  'function revealArtLayer(uint256 tokenId, uint256 layerIndex) public',
  'function castVote(uint256 tokenId, uint256 optionIndex) public',
  'function completeAchievementTier(uint256 tokenId, uint256 tierIndex) public',
  'function unlockEducationalModule(uint256 tokenId, uint256 moduleIndex) public'
];

export class SpecializedCapsuleService {
  private client: LegacyChainClient;
  private contractAddress: string;

  constructor(client: LegacyChainClient) {
    this.client = client;
    this.contractAddress = process.env.NEXT_PUBLIC_LEGACY_SPECIALIZED_CAPSULES_ADDRESS || '';
  }

  // Film Capsule
  async createFilmCapsule(input: CreateFilmCapsuleInput): Promise<SpecializedCapsule> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);
    
    // Upload metadata to IPFS
    const tokenURI = await this.client.ipfs.uploadJSON({
      name: input.title,
      description: `Film by ${input.director}`,
      image: input.posterURI || '',
      attributes: [
        { trait_type: 'Type', value: 'Film Capsule' },
        { trait_type: 'Director', value: input.director },
        { trait_type: 'Premiere Date', value: new Date(input.premiereDate).toISOString() }
      ]
    }, input.title);

    const tx = await contract.createFilmCapsule(
      input.recipient,
      input.title,
      input.director,
      Math.floor(input.premiereDate.getTime() / 1000),
      input.trailerURI,
      input.encryptedFilmURI,
      tokenURI
    );
    
    const receipt = await tx.wait();
    const tokenId = receipt.logs[0].args.tokenId.toString();

    const capsule: SpecializedCapsule = {
      id: tokenId,
      type: SpecializedCapsuleType.FILM,
      owner: input.recipient,
      metadata: {
        title: input.title,
        director: input.director,
        premiereDate: input.premiereDate,
        trailerURI: input.trailerURI
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Store in backend
    await this.client.makeRequest('POST', '/specialized-capsules', capsule);
    
    return capsule;
  }

  // Creator Message Capsule
  async createCreatorMessageCapsule(input: CreateCreatorMessageInput): Promise<SpecializedCapsule> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);
    
    // Encrypt message if needed
    let encryptedMessage = input.encryptedMessage;
    if (input.encrypt && input.message) {
      const password = input.password || this.client.crypto.generatePassword();
      const { encrypted } = await this.client.crypto.encrypt(input.message, password);
      encryptedMessage = encrypted;
    }

    const tokenURI = await this.client.ipfs.uploadJSON({
      name: `Message from ${input.creator}`,
      description: input.publicTeaser,
      attributes: [
        { trait_type: 'Type', value: 'Creator Message' },
        { trait_type: 'Creator', value: input.creator }
      ]
    }, `creator-message-${Date.now()}`);

    const tx = await contract.createCreatorMessageCapsule(
      input.recipient,
      input.creator,
      encryptedMessage,
      input.publicTeaser,
      Math.floor(input.revealTime.getTime() / 1000),
      tokenURI
    );
    
    const receipt = await tx.wait();
    const tokenId = receipt.logs[0].args.tokenId.toString();

    return {
      id: tokenId,
      type: SpecializedCapsuleType.CREATOR_MESSAGE,
      owner: input.recipient,
      metadata: {
        creator: input.creator,
        publicTeaser: input.publicTeaser,
        revealTime: input.revealTime
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Mystery Trait Capsule
  async createMysteryTraitCapsule(input: CreateMysteryTraitInput): Promise<SpecializedCapsule> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);

    const tokenURI = await this.client.ipfs.uploadJSON({
      name: input.name,
      description: `Mystery traits unlock over time`,
      attributes: [
        { trait_type: 'Type', value: 'Mystery Trait' },
        { trait_type: 'Total Traits', value: input.hiddenTraits.length }
      ]
    }, input.name);

    const unlockTimestamps = input.unlockTimes.map(time => Math.floor(time.getTime() / 1000));

    const tx = await contract.createMysteryTraitCapsule(
      input.recipient,
      input.name,
      input.hiddenTraits,
      unlockTimestamps,
      tokenURI
    );
    
    const receipt = await tx.wait();
    const tokenId = receipt.logs[0].args.tokenId.toString();

    return {
      id: tokenId,
      type: SpecializedCapsuleType.MYSTERY_TRAIT,
      owner: input.recipient,
      metadata: {
        name: input.name,
        totalTraits: input.hiddenTraits.length,
        unlockTimes: input.unlockTimes
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // VIP Access Capsule
  async createVIPAccessCapsule(input: CreateVIPAccessInput): Promise<SpecializedCapsule> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);

    const tokenURI = await this.client.ipfs.uploadJSON({
      name: `VIP Access: ${input.eventId}`,
      description: input.description,
      attributes: [
        { trait_type: 'Type', value: 'VIP Access' },
        { trait_type: 'Event', value: input.eventId },
        { trait_type: 'Usage Limit', value: input.usageLimit }
      ]
    }, `vip-${input.eventId}`);

    const tx = await contract.createVIPAccessCapsule(
      input.recipient,
      input.eventId,
      input.description,
      input.encryptedAccessData,
      Math.floor(input.expiryTime.getTime() / 1000),
      input.usageLimit,
      tokenURI
    );
    
    const receipt = await tx.wait();
    const tokenId = receipt.logs[0].args.tokenId.toString();

    return {
      id: tokenId,
      type: SpecializedCapsuleType.VIP_ACCESS,
      owner: input.recipient,
      metadata: {
        eventId: input.eventId,
        description: input.description,
        expiryTime: input.expiryTime,
        usageLimit: input.usageLimit
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Art Provenance Capsule
  async createArtProvenanceCapsule(input: CreateArtProvenanceInput): Promise<SpecializedCapsule> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);

    const tokenURI = await this.client.ipfs.uploadJSON({
      name: `${input.artist} - Evolving Artwork`,
      description: 'Artwork that reveals new layers over time',
      image: input.baseArtworkURI,
      attributes: [
        { trait_type: 'Type', value: 'Art Provenance' },
        { trait_type: 'Artist', value: input.artist },
        { trait_type: 'Total Layers', value: input.layerURIs.length }
      ]
    }, `art-${Date.now()}`);

    const revealTimestamps = input.revealTimes.map(time => Math.floor(time.getTime() / 1000));

    const tx = await contract.createArtProvenanceCapsule(
      input.recipient,
      input.artist,
      input.baseArtworkURI,
      input.layerURIs,
      input.layerDescriptions,
      revealTimestamps,
      tokenURI
    );
    
    const receipt = await tx.wait();
    const tokenId = receipt.logs[0].args.tokenId.toString();

    return {
      id: tokenId,
      type: SpecializedCapsuleType.ART_PROVENANCE,
      owner: input.recipient,
      metadata: {
        artist: input.artist,
        baseArtworkURI: input.baseArtworkURI,
        totalLayers: input.layerURIs.length,
        revealTimes: input.revealTimes
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Voting Capsule
  async createVotingCapsule(input: CreateVotingCapsuleInput): Promise<SpecializedCapsule> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);

    const tokenURI = await this.client.ipfs.uploadJSON({
      name: `Vote: ${input.proposalTitle}`,
      description: `Voting ends at ${input.endTime.toISOString()}`,
      attributes: [
        { trait_type: 'Type', value: 'Voting Capsule' },
        { trait_type: 'Proposal', value: input.proposalTitle },
        { trait_type: 'Options', value: input.options.length },
        { trait_type: 'Vote Weight', value: input.voteWeight }
      ]
    }, `vote-${Date.now()}`);

    const tx = await contract.createVotingCapsule(
      input.voter,
      input.proposalTitle,
      input.options,
      Math.floor(input.endTime.getTime() / 1000),
      input.voteWeight,
      tokenURI
    );
    
    const receipt = await tx.wait();
    const tokenId = receipt.logs[0].args.tokenId.toString();

    return {
      id: tokenId,
      type: SpecializedCapsuleType.VOTING,
      owner: input.voter,
      metadata: {
        proposalTitle: input.proposalTitle,
        options: input.options,
        endTime: input.endTime,
        voteWeight: input.voteWeight
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Music Capsule
  async createMusicCapsule(input: CreateMusicCapsuleInput): Promise<SpecializedCapsule> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);

    const tokenURI = await this.client.ipfs.uploadJSON({
      name: `${input.trackTitle} by ${input.artist}`,
      description: `Exclusive track releasing on ${input.releaseDate.toISOString()}`,
      attributes: [
        { trait_type: 'Type', value: 'Music Capsule' },
        { trait_type: 'Artist', value: input.artist },
        { trait_type: 'Track', value: input.trackTitle }
      ]
    }, `music-${Date.now()}`);

    const tx = await contract.createMusicCapsule(
      input.recipient,
      input.trackTitle,
      input.artist,
      input.encryptedTrackURI,
      input.previewURI,
      Math.floor(input.releaseDate.getTime() / 1000),
      tokenURI
    );
    
    const receipt = await tx.wait();
    const tokenId = receipt.logs[0].args.tokenId.toString();

    return {
      id: tokenId,
      type: SpecializedCapsuleType.MUSIC,
      owner: input.recipient,
      metadata: {
        trackTitle: input.trackTitle,
        artist: input.artist,
        releaseDate: input.releaseDate,
        previewURI: input.previewURI
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Achievement Capsule
  async createAchievementCapsule(input: CreateAchievementCapsuleInput): Promise<SpecializedCapsule> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);

    const tokenURI = await this.client.ipfs.uploadJSON({
      name: input.achievementName,
      description: `Gaming achievement with ${input.tiers.length} tiers`,
      attributes: [
        { trait_type: 'Type', value: 'Achievement Capsule' },
        { trait_type: 'Achievement', value: input.achievementName },
        { trait_type: 'Tiers', value: input.tiers.length }
      ]
    }, `achievement-${Date.now()}`);

    const tx = await contract.createAchievementCapsule(
      input.player,
      input.achievementName,
      input.tiers,
      input.requirements,
      input.rewards,
      tokenURI
    );
    
    const receipt = await tx.wait();
    const tokenId = receipt.logs[0].args.tokenId.toString();

    return {
      id: tokenId,
      type: SpecializedCapsuleType.ACHIEVEMENT,
      owner: input.player,
      metadata: {
        achievementName: input.achievementName,
        tiers: input.tiers,
        requirements: input.requirements,
        rewards: input.rewards
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Educational Capsule
  async createEducationalCapsule(input: CreateEducationalCapsuleInput): Promise<SpecializedCapsule> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);

    const tokenURI = await this.client.ipfs.uploadJSON({
      name: input.courseName,
      description: `Educational course with ${input.modules.length} modules`,
      attributes: [
        { trait_type: 'Type', value: 'Educational Capsule' },
        { trait_type: 'Course', value: input.courseName },
        { trait_type: 'Modules', value: input.modules.length }
      ]
    }, `edu-${Date.now()}`);

    const tx = await contract.createEducationalCapsule(
      input.student,
      input.courseName,
      input.modules,
      input.unlockDays,
      input.contentURIs,
      tokenURI
    );
    
    const receipt = await tx.wait();
    const tokenId = receipt.logs[0].args.tokenId.toString();

    return {
      id: tokenId,
      type: SpecializedCapsuleType.EDUCATIONAL,
      owner: input.student,
      metadata: {
        courseName: input.courseName,
        modules: input.modules,
        unlockDays: input.unlockDays
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Memory Capsule
  async createMemoryCapsule(input: CreateMemoryCapsuleInput): Promise<SpecializedCapsule> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);

    const tokenURI = await this.client.ipfs.uploadJSON({
      name: input.title,
      description: `Memory capsule unlocking on ${input.unlockTime.toISOString()}`,
      attributes: [
        { trait_type: 'Type', value: 'Memory Capsule' },
        { trait_type: 'Title', value: input.title },
        { trait_type: 'Inheritors', value: input.inheritors.length }
      ]
    }, `memory-${Date.now()}`);

    const tx = await contract.createMemoryCapsule(
      input.recipient,
      input.title,
      input.encryptedMemoryURI,
      Math.floor(input.unlockTime.getTime() / 1000),
      input.inheritors,
      tokenURI
    );
    
    const receipt = await tx.wait();
    const tokenId = receipt.logs[0].args.tokenId.toString();

    return {
      id: tokenId,
      type: SpecializedCapsuleType.MEMORY,
      owner: input.recipient,
      metadata: {
        title: input.title,
        unlockTime: input.unlockTime,
        inheritors: input.inheritors
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Get capsule details
  async getCapsule(capsuleId: string): Promise<SpecializedCapsule> {
    const response = await this.client.makeRequest<SpecializedCapsule>('GET', `/specialized-capsules/${capsuleId}`);
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch capsule');
    }

    return response.data;
  }

  // List capsules
  async listCapsules(params?: PaginationParams & { type?: SpecializedCapsuleType }): Promise<PaginatedResponse<SpecializedCapsule>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.type) queryParams.append('type', params.type);
    }

    const response = await this.client.makeRequest<PaginatedResponse<SpecializedCapsule>>(
      'GET',
      `/specialized-capsules?${queryParams.toString()}`
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch capsules');
    }

    return response.data;
  }

  // Interaction methods
  async unlockTrait(capsuleId: string, traitIndex: number): Promise<void> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);
    
    const tx = await contract.unlockTrait(capsuleId, traitIndex);
    await tx.wait();
  }

  async useVIPAccess(capsuleId: string): Promise<string> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);
    
    const tx = await contract.useVIPAccess(capsuleId);
    const receipt = await tx.wait();
    
    // Extract access data from events
    const event = receipt.logs.find((log: any) => log.eventName === 'VIPAccessUsed');
    return event?.args?.accessData || '';
  }

  async castVote(capsuleId: string, optionIndex: number): Promise<void> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_SPECIALIZED_CAPSULES_ABI, signer);
    
    const tx = await contract.castVote(capsuleId, optionIndex);
    await tx.wait();
  }
} 